import { FaroIconButton, FaroOverlayTrigger, FaroTable, FaroTooltip } from '@faro/design-system';
import { CollapseAllIcon, ExpandAllIcon } from '@faro/icons';
import { ComponentPropsWithRef, ReactNode, useRef, useState } from 'react';
import useSortableTable from '../../package/react-hooks/useSortableTable';
import { StudySpaceAnalyticEventKey, useStudySpaceAnalytics } from '../../util/analytics';
import { StudyDefinitionTableRowCallbacks } from './StudyDefinitionMenu/StudyDefinitionMenu';
import styles from './StudyDefinitionTable.module.scss';
import { StudyDefinitionTableSkeleton } from './StudyDefinitionTable.skeleton';
import { StudyDefinitionTableRow } from './StudyDefinitionTable.types';
import { StudyDefinitionRow, StudyDefinitionRowRef } from './StudyDefinitionTableRow/StudyDefinitionTableRow';

export interface StudyDefinitionTableProps extends ComponentPropsWithRef<'table'>, StudyDefinitionTableRowCallbacks {
    rows: StudyDefinitionTableRow[];
}

function StudyDefinitionTable(props: StudyDefinitionTableProps): ReactNode {
    const {
        rows,
        onRowClick,
        onStatusTypeUpdate,
        onExport,
        onClone,
        onRename,
        onMove,
        onDelete,
        onCreateSubstudy,
        className = '',
        ref,
        ...rest
    } = props;

    const { trackEvent } = useStudySpaceAnalytics();

    // Store collapsed rows instead of expanded rows - all rows start expanded by default
    const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());

    // Refs to track each row for child sorting
    const rowRefs = useRef<Map<string, StudyDefinitionRowRef>>(new Map());

    // Helper function to collect only master study IDs (rows that can have substudies)
    const getMasterStudyIds = (rowsToProcess: StudyDefinitionTableRow[]): string[] => {
        return rowsToProcess.filter(row => row.childRows && row.childRows.length > 0).map(row => row.id);
    };

    const toggleRowExpansion = (row: StudyDefinitionTableRow) => {
        trackEvent(StudySpaceAnalyticEventKey.expandCollapseStudyDefinition, {
            definitionType: 'main',
            resultingState: collapsedRows.has(row.id) ? 'expanded' : 'collapsed',
        });

        const newCollapsedRows = new Set(collapsedRows);
        if (newCollapsedRows.has(row.id)) {
            newCollapsedRows.delete(row.id); // Expand
        } else {
            newCollapsedRows.add(row.id); // Collapse
        }
        setCollapsedRows(newCollapsedRows);
    };

    // Function to expand/collapse all rows
    const handleExpandCollapseAll = () => {
        trackEvent(StudySpaceAnalyticEventKey.expandCollapseAllStudyDefinitions, {
            definitionType: 'main',
            resultingState: collapsedRows.size === 0 ? 'collapsed' : 'expanded',
        });

        if (collapsedRows.size === 0) {
            // All rows are expanded, collapse all
            const masterStudyIds = getMasterStudyIds(rows);
            setCollapsedRows(new Set(masterStudyIds));
        } else {
            // Some or all rows are collapsed, expand all
            setCollapsedRows(new Set());
        }
    };

    // Check if all rows are expanded
    const allRowsExpanded = (() => {
        const masterStudyIds = getMasterStudyIds(rows);
        return masterStudyIds.every(id => !collapsedRows.has(id));
    })();

    const rowsWithExpandInfo = (() => {
        const addExpansionInfo = (rowList: StudyDefinitionTableRow[]): StudyDefinitionTableRow[] => {
            return rowList.map(row => ({
                ...row,
                isExpanded: !collapsedRows.has(row.id),
                childRows: row.childRows ? addExpansionInfo(row.childRows) : undefined,
            }));
        };

        return addExpansionInfo(rows);
    })();

    const {
        items: sortedRows,
        sortDataBy,
        sortIconBy,
        sortConfig,
    } = useSortableTable(rowsWithExpandInfo, {
        keys: ['updated'],
        direction: 'desc',
    });

    // Callback to register a row ref
    const registerRowRef = (rowId: string, rowRef: StudyDefinitionRowRef | null) => {
        if (rowRef) {
            rowRefs.current.set(rowId, rowRef);
        } else {
            rowRefs.current.delete(rowId);
        }
    };

    // Enhanced sortDataBy that also sorts child rows
    const enhancedSortDataBy = (keys: string[]) => {
        trackEvent(StudySpaceAnalyticEventKey.sortStudyDefinitionTable, {
            columnName: keys[0],
            sortOrder: sortConfig?.direction === 'asc' ? 'desc' : 'asc',
        });

        // Call the original sortDataBy
        sortDataBy(keys);

        // Also trigger child row sorting in all rows
        const currentSortConfig = sortConfig || { keys: ['updated'], direction: 'desc' as const };
        const newDirection =
            currentSortConfig.keys.join(',') === keys.join(',') && currentSortConfig.direction === 'asc'
                ? 'desc'
                : 'asc';

        rowRefs.current.forEach(rowRef => {
            rowRef.reSortChildren(keys, newDirection);
        });
    };

    const [cloning, setCloning] = useState(false);

    const onCloneInternal = async (row: StudyDefinitionTableRow) => {
        setCloning(true);
        try {
            await onClone?.(row);
        } finally {
            setCloning(false);
        }
    };

    return (
        <FaroTable
            {...rest}
            ref={ref}
            className={`
                ${styles.table}
                ${className}
            `}
            appearance="bordered-minimally-interactive"
            data-automation-id="studySpaceStudyDefinitionsTable"
            data-testid="studySpaceStudyDefinitionsTable"
        >
            <thead>
                <tr>
                    <th>
                        <div className={styles.headerContent}>
                            <FaroOverlayTrigger
                                container={document.body}
                                overlay={
                                    <FaroTooltip className={styles.expandAllTooltip} inverted>
                                        {allRowsExpanded ? 'Collapse all' : 'Expand all'}
                                    </FaroTooltip>
                                }
                                placement="top"
                            >
                                <FaroIconButton
                                    className={styles.expandAllButton}
                                    icon={allRowsExpanded ? <CollapseAllIcon /> : <ExpandAllIcon />}
                                    onClick={handleExpandCollapseAll}
                                    appearance="inline"
                                    size="small"
                                />
                            </FaroOverlayTrigger>
                        </div>
                    </th>
                    <th onClick={() => enhancedSortDataBy(['name'])} data-testid="studyDefinitionTitleButton">
                        <div className={styles.headerContent}>Study Definition Title {sortIconBy('name')}</div>
                    </th>
                    <th onClick={() => enhancedSortDataBy(['status.type', 'isDeleted'])}>
                        <div className={styles.headerContent}>Status {sortIconBy('status.type')}</div>
                    </th>
                    <th onClick={() => enhancedSortDataBy(['originLabel'])}>
                        <div className={styles.headerContent}>Origin {sortIconBy('originLabel')}</div>
                    </th>
                    <th onClick={() => enhancedSortDataBy(['updated'])}>
                        <div className={styles.headerContent}>Last Updated {sortIconBy('updated')}</div>
                    </th>
                    <th></th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {sortedRows.map((row: StudyDefinitionTableRow, index: number) => (
                    <StudyDefinitionRow
                        key={row.id}
                        index={index}
                        row={row}
                        onRowClick={onRowClick}
                        onStatusTypeUpdate={onStatusTypeUpdate}
                        onExport={onExport}
                        onClone={onCloneInternal}
                        onRename={onRename}
                        onMove={onMove}
                        onDelete={onDelete}
                        onCreateSubstudy={onCreateSubstudy}
                        cloneDisabled={cloning}
                        onToggleExpansion={toggleRowExpansion}
                        sortConfig={sortConfig}
                        ref={(rowRef: StudyDefinitionRowRef) => registerRowRef(row.id, rowRef)}
                        data-automation-role="studyDefinitionRow"
                        data-intercom-target="Select Study Definition"
                        data-testid={`studyDefinitionRow-${index}`}
                    />
                ))}
            </tbody>
        </FaroTable>
    );
}

StudyDefinitionTable.Skeleton = StudyDefinitionTableSkeleton;

export default StudyDefinitionTable;
